import { Link } from "wouter";
import { useFavorites, useRecentTools } from "@/hooks/useFavorites";
import { getToolById } from "@/lib/tools-data";
import { FavoriteButton } from "@/components/FavoriteButton";
import { useLocale } from "@/hooks/useLocale";
import { Star, Clock } from "lucide-react";

function ToolChip({ toolId }: { toolId: string }) {
  const tool = getToolById(toolId);
  if (!tool || tool.hidden) return null;
  return (
    <Link href={`/tool/${tool.id}`}>
      <div className="flex items-center gap-2 pl-3 pr-2 py-2 rounded-full border border-border bg-card hover:border-primary/30 transition-colors cursor-pointer">
        <span className="text-sm">{tool.name}</span>
        <FavoriteButton toolId={tool.id} />
      </div>
    </Link>
  );
}

/** Only renders once there's something real to show -- entirely invisible
 * to first-time visitors, so it never adds clutter to a clean first visit. */
export function FavoritesAndRecent() {
  const { favorites } = useFavorites();
  const { recent } = useRecentTools();
  const { strings } = useLocale();

  if (favorites.length === 0 && recent.length === 0) return null;

  return (
    <div className="container py-6 space-y-4">
      {favorites.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <Star className="h-3 w-3" /> {strings.home.yourFavorites}
          </p>
          <div className="flex flex-wrap gap-2">
            {favorites.slice(0, 2).map(id => <ToolChip key={id} toolId={id} />)}
          </div>
        </div>
      )}
      {recent.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <Clock className="h-3 w-3" /> {strings.home.recentlyUsed}
          </p>
          <div className="flex flex-wrap gap-2">
            {recent.slice(0, 2).map(id => <ToolChip key={id} toolId={id} />)}
          </div>
        </div>
      )}
    </div>
  );
}
